function collectText(node, parts) {
    if (!node || typeof node !== 'object') return;

    if (typeof node.text === 'string') {
        parts.push(node.text);
    }

    if (Array.isArray(node.content)) {
        node.content.forEach((child) => collectText(child, parts));
    }
}

function collectWikidataEntities(node, entitiesById, entitiesByLabel) {
    if (!node || typeof node !== 'object') return;

    const marks = Array.isArray(node.marks) ? node.marks : [];
    marks.forEach((mark) => {
        if (mark?.type !== 'wikidataEntity') return;

        const attrs = mark.attrs || {};
        const id = typeof attrs.id === 'string' ? attrs.id.trim() : '';
        const label = typeof attrs.label === 'string' ? attrs.label.trim() : '';

        if (id && label && !entitiesById.has(id)) {
            entitiesById.set(id, label);
            return;
        }

        if (!id && label && !entitiesByLabel.has(label.toLowerCase())) {
            entitiesByLabel.set(label.toLowerCase(), label);
        }
    });

    if (Array.isArray(node.content)) {
        node.content.forEach((child) => collectWikidataEntities(child, entitiesById, entitiesByLabel));
    }
}

/** Default cap for Claims delta — each entity expands corpus segment + LLM work. */
export const DELTA_CLAIMS_MAX_LINKED_ENTITIES = 8;

function collectLinkedWikidataCounts(node, countsById) {
    if (!node || typeof node !== 'object') return;

    const marks = Array.isArray(node.marks) ? node.marks : [];
    marks.forEach((mark) => {
        if (mark?.type !== 'wikidataEntity') return;
        const attrs = mark.attrs || {};
        const id = typeof attrs.id === 'string' ? attrs.id.trim() : '';
        if (!id) return;
        const label = typeof attrs.label === 'string' ? attrs.label.trim() : '';
        const prev = countsById.get(id);
        if (prev) {
            prev.count += 1;
            if (!prev.label && label) prev.label = label;
        } else {
            countsById.set(id, {id, label: label || id, count: 1});
        }
    });

    if (Array.isArray(node.content)) {
        node.content.forEach((child) => collectLinkedWikidataCounts(child, countsById));
    }
}

/**
 * Wikidata Q-IDs linked in the FINTEL body, ranked by mention count (most cited first).
 * Used for Claims delta where the API requires entity ids and cost scales with entity count.
 */
export function extractLinkedWikidataEntitiesForDelta(content, maxEntities = DELTA_CLAIMS_MAX_LINKED_ENTITIES) {
    const countsById = new Map();
    collectLinkedWikidataCounts(content, countsById);
    return Array.from(countsById.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, Math.max(1, maxEntities))
        .map(({id, label}) => ({id, label: label || id}));
}

export function extractEntitiesFromContent(content, maxEntities = 20) {
    // Primary path: explicit wikidataEntity marks in the ProseMirror document.
    const entitiesById = new Map();
    const entitiesByLabel = new Map();
    collectWikidataEntities(content, entitiesById, entitiesByLabel);

    const fromMarks = [
        ...Array.from(entitiesById.entries()).map(([id, label]) => ({id, label})),
        ...Array.from(entitiesByLabel.values()).map((label) => ({id: null, label})),
    ].slice(0, maxEntities);

    if (fromMarks.length > 0) {
        return fromMarks;
    }

    // Fallback path: lightweight title-case extraction when marks are missing.
    const parts = [];
    collectText(content, parts);

    const fullText = parts.join(' ');
    const candidates = fullText.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2}\b/g) || [];

    const blocked = new Set([
        'The',
        'This',
        'That',
        'These',
        'Those',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ]);

    const counts = new Map();

    candidates.forEach((raw) => {
        const name = raw.trim();
        if (!name || blocked.has(name)) return;
        counts.set(name, (counts.get(name) || 0) + 1);
    });

    return Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, maxEntities)
        .map(([name]) => ({id: null, label: name}));
}

/**
 * Flatten ProseMirror/TipTap JSON to plain text for report-aware delta clustering.
 */
export function extractPlainTextFromContent(content) {
    if (!content) return '';
    const parts = [];
    collectText(content, parts);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
}

